// StateChip — the small pill that shows an EA/GA/etc state. Central
// styling so every surface renders it the same way (feature announcement
// on the Timeline, feed card badge, release item label, etc).
//
// Wrap in <WorkspaceHoverWrap> when the chip has a flag context; then
// hovering shows workspaces and clicking opens the workspace modal.
import { cn } from "@/lib/cn";

export type StateKind = "ga" | "ea" | "in_development" | "unreleased" | "disabled";

const KIND_TO_CLASS: Record<StateKind, string> = {
  ga: "bg-ga-soft text-ga",
  ea: "bg-ea-soft text-ea",
  in_development: "bg-nightly-soft text-nightly",
  unreleased: "bg-nightly-soft text-nightly",
  disabled: "bg-border text-text-tertiary",
};

export function StateChip({
  kind,
  label,
  size = "sm",
  className,
  detail,
  date,
}: {
  kind: StateKind;
  /** Display text. Defaults to the uppercased kind. */
  label?: string;
  size?: "xs" | "sm" | "md";
  className?: string;
  /** Optional middle segment rendered between the label and the date, e.g.
   *  an EA workspace count ("12/34 workspaces") or a small spinner while it
   *  loads. Reads as "EA · 12/34 workspaces · May 1" inside one pill. */
  detail?: React.ReactNode;
  /** Optional milestone date appended inside the same pill, e.g. a feature's
   *  EA/GA date. `projected` renders it muted + italic so a coarse target
   *  ("Q3 2026") never reads as a firm commitment. */
  date?: { value: string; projected?: boolean };
}) {
  // The default `sm` carries the consumer's whole availability answer
  // (EA/GA · workspaces · date), so it sits at the 11px legibility floor even
  // though dense author-side metadata may run smaller. `xs` (9.5px) stays the
  // documented Micro step, reserved for genuinely tight inline runs.
  const sizeCls =
    size === "md"
      ? "px-2.5 py-1 text-xs"
      : size === "xs"
        ? "px-1.5 py-0.5 text-[9.5px]"
        : "px-2 py-0.5 text-[11px]";
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full font-semibold uppercase tracking-wider",
        sizeCls,
        KIND_TO_CLASS[kind],
        className,
      )}
    >
      {label ?? kind.toUpperCase()}
      {detail != null && (
        <span className="ml-1 inline-flex items-center font-normal normal-case tracking-normal opacity-80">
          · {detail}
        </span>
      )}
      {date && (
        <span
          className={cn(
            "ml-1 font-normal normal-case tracking-normal",
            date.projected ? "italic opacity-70" : "opacity-80",
          )}
        >
          · {date.value}
        </span>
      )}
    </span>
  );
}
